import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import PersonsList from "./PersonsList";
import PersonModalForm from "./PersonModalForm";
import IDCardPrint from "./IDCardPrint";
import Billede1 from "../../../Images/Billede1.jpg";
import { Person, EventDto, Zone, PrintPerson, FunctionWithZones } from "./types";
import { FunctionOption } from "./types";
import "./Persons.css";
import { getAllowedEventIds } from "../../../api/shiftsApi";
import { getAuthHeaders } from "../../../api/authHelper";

interface Template {
    id: number;
    name: string;
    fields: any[];
    background?: string;
}

interface PagedResponse<T> {
    items: T[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
}

type Filters = {
    search: string;
    eventId: number | null;
    functionId: number | null;
    country: string;
    dateFrom: string;
    dateTo: string;
    profileFilter: "all" | "with" | "without";
    printFilter: "all" | "printed" | "notPrinted";
};

const initialFilters: Filters = {
    search: "",
    eventId: null,
    functionId: null,
    country: "",
    dateFrom: "",
    dateTo: "",
    profileFilter: "all",
    printFilter: "all",
};

type PersonFormData = Omit<Person, "functions"> & {
    functions: FunctionOption[];
    uploadImageBase64?: string;
};

function normalizePersonFromApi(p: any): Person {
    return {
        ...p,
        events: p.events ?? [],
        functions: (p.functions ?? []).map((f: any) => ({
            functionId: f.functionId,
            functionName: f.functionName,
            eventId: f.eventId
        })),
        zones: p.zones ?? [],
        hasProfileImage: p.hasImage ?? !!p.personImagePath,
        personImagePath: p.personImagePath ?? "",
        profileUrl: p.personImagePath ?? "",
    };
}

function getRoles(): string[] {
    const raw = sessionStorage.getItem("userRoles") || localStorage.getItem("userRoles");
    if (!raw) return [];

    try {
        const roles = JSON.parse(raw);
        return Array.isArray(roles) ? roles : [];
    } catch {
        return raw.split(",").map((r) => r.trim()).filter(Boolean);
    }
}

const Persons: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const currentUserId = Number(sessionStorage.getItem("userId") || localStorage.getItem("userId") || 0);

    const roles = useMemo(() => getRoles(), []);
    const isAdmin = roles.includes("Admin");
    const isClub = roles.includes("Club") && !isAdmin;

    const [persons, setPersons] = useState<Person[]>([]);
    const [events, setEvents] = useState<EventDto[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [allFunctions, setAllFunctions] = useState<FunctionOption[]>([]);
    const [functionsWithZones, setFunctionsWithZones] = useState<FunctionWithZones[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);

    const [filters, setFilters] = useState<Filters>(initialFilters);
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [addingPerson, setAddingPerson] = useState(false);

    const [selectedPersonsForIDCard, setSelectedPersonsForIDCard] = useState<Person[]>([]);
    const [targetEventId, setTargetEventId] = useState<number | null>(null);
    const [targetFunctionIds, setTargetFunctionIds] = useState<number[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [showIDCardPreview, setShowIDCardPreview] = useState(false);
    const [printedIds, setPrintedIds] = useState<number[] | undefined>(undefined);
    const getFunctionsForEvent = (eventId: number | null) =>
        !eventId
            ? []
            : functionsWithZones
                .filter(f => f.eventID === eventId)
                .sort((a, b) => a.functionName.localeCompare(b.functionName));

    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [refreshKey, setRefreshKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>("");

    const [pendingPerson, setPendingPerson] = useState<PersonFormData | null>(null);
    const [duplicateCandidates, setDuplicateCandidates] = useState<Person[] | undefined>(undefined);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    const [debouncedSearch] = useDebouncedValue(filters.search, 300);

    const editRequestRef = useRef(0);


    useEffect(() => {
        setPage(1);
    }, [
        debouncedSearch,
        filters.eventId,
        filters.functionId,
        filters.country,
        filters.dateFrom,
        filters.dateTo,
        filters.profileFilter,
        filters.printFilter,
    ]);



    useEffect(() => {
        const fetchBaseData = async () => {
            setLoading(true);
            setErrorMsg("");

            try {
                const [eventsRes, functionsRes, templatesRes, countriesRes] = await Promise.all([
                    fetch(`${apiUrl}/api/events/light`,{
                        headers: getAuthHeaders(),
                    }),
                    fetch(`${apiUrl}/api/function`,{
                        headers: getAuthHeaders(),
                    }),
                    fetch(`${apiUrl}/api/IDTemplate`, {
                        headers: getAuthHeaders(),
                    }),
                    fetch(`${apiUrl}/api/persons/countries`, {
                        headers: getAuthHeaders(),
                    })
                ]);

                if (!eventsRes.ok || !functionsRes.ok || !templatesRes.ok || !countriesRes.ok) {
                    throw new Error("Kunne ikke hente data fra API");
                }

                const eventsData: EventDto[] = await eventsRes.json();
                let finalEvents = eventsData;

                if (isClub) {
                    const allowedIds = await getAllowedEventIds(apiUrl, currentUserId);
                    const allowedSet = new Set(allowedIds);
                    finalEvents = eventsData.filter((ev) => allowedSet.has(ev.eventID));
                }

                setEvents(finalEvents);

                if (isClub && finalEvents.length > 0) {
                    setFilters((f) => {
                        if (f.eventId) return f;

                        return {
                            ...f,
                            eventId: finalEvents[0].eventID
                        };
                    });
                } else {
                    setFilters((f) =>
                        f.eventId &&
                            !finalEvents.some((e) => e.eventID === f.eventId)
                            ? { ...f, eventId: null}
                            : f
                    );
                }

                const functionsData: {
                    functionId: number;
                    functionName: string;
                    useDesign2?: boolean;
                    categoryName: string;
                    eventID: number;
                }[] = await functionsRes.json();

                setAllFunctions(
                    functionsData.map((f) => ({
                        functionId: f.functionId,
                        functionName: f.functionName,
                        categoryName: f.categoryName,
                        eventID: f.eventID
                    }))
                );

                const functionsZonesData: FunctionWithZones[] = await Promise.all(
                    functionsData.map(async (f) => {
                        const [zonesRes, funcRes] = await Promise.all([
                            fetch(`${apiUrl}/api/zones/functions/byfunction/${f.functionId}`, {
                                headers: getAuthHeaders(),
                            }),
                            fetch(`${apiUrl}/api/function/${f.functionId}`),
                        ]);

                        const zones: Zone[] = zonesRes.ok ? await zonesRes.json() : [];
                        const funcData = funcRes.ok
                            ? await funcRes.json()
                            : { eventID: null, useDesign2: false };

                        return {
                            functionId: f.functionId,
                            functionName: f.functionName,
                            eventID: funcData.eventID,
                            useDesign2: funcData.useDesign2 ?? f.useDesign2 ?? false,
                            assignedZones: zones,
                        };
                    })
                );

                setFunctionsWithZones(functionsZonesData);
                setTemplates(await templatesRes.json());
                setCountries(await countriesRes.json());
            } catch (err) {
                console.error(err);
                setErrorMsg("Der skete en fejl ved hentning af data.");
            } finally {
                setLoading(false);
            }
        };

        fetchBaseData();
    }, [apiUrl, currentUserId, isClub]);

    useEffect(() => {
        const fetchPersons = async () => {
            setLoadingPersons(true);
            setErrorMsg("");

            try {
                const params = new URLSearchParams();

                params.set("page", String(page));
                params.set("pageSize", String(pageSize));

                if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
                if (filters.eventId) {
                    params.set("eventId", String(filters.eventId));
                }
                if (filters.functionId) {
                    params.set("functionId", String(filters.functionId));
                }
                if (filters.country) params.set("country", filters.country);
                if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
                if (filters.dateTo) params.set("dateTo", filters.dateTo);

                if (filters.profileFilter === "with") params.set("onlyWithProfile", "true");
                if (filters.profileFilter === "without") params.set("onlyWithoutProfile", "true");
                if (filters.printFilter === "printed") params.set("onlyPrinted", "true");
                if (filters.printFilter === "notPrinted") params.set("onlyNotPrinted", "true");

                const selectedEventForPrinted = events.find(
                    (e) => e.eventID === filters.eventId
                );

                if (
                    (filters.printFilter === "printed" || filters.printFilter === "notPrinted") &&
                    selectedEventForPrinted
                ) {
                    params.set("printedEventId", String(selectedEventForPrinted.eventID));
                }

                const res = await fetch(`${apiUrl}/api/persons?${params.toString()}`, {
                    headers: getAuthHeaders(),
                });

                if (!res.ok) {
                    throw new Error("Kunne ikke hente personer");
                }

                const data: PagedResponse<any> = await res.json();

                setPersons((data.items ?? []).map(normalizePersonFromApi));
                setTotalCount(data.totalCount ?? 0);
                setTotalPages(data.totalPages ?? 1);
            } catch (err) {
                console.error(err);
                setErrorMsg("Der skete en fejl ved hentning af personer.");
            } finally {
                setLoadingPersons(false);
            }
        };

        fetchPersons();
    }, [
        apiUrl,
        page,
        pageSize,
        debouncedSearch,
        filters.eventId,
        filters.functionId,
        filters.country,
        filters.dateFrom,
        filters.dateTo,
        filters.profileFilter,
        filters.printFilter,
        events,
        refreshKey
    ]);

    useEffect(() => {
        fetch(`${apiUrl}/api/persons/printed`, {
            headers: getAuthHeaders(),
        })
            .then((res) => res.json())
            .then((data: { personId: number }[]) => {
                setPrintedIds(data.map((d) => d.personId));
            })
            .catch((err) => console.error("Failed to load printed persons", err));
    }, [apiUrl]);

    useEffect(() => {
        setSelectedPersonsForIDCard([]);
    }, [filters, page]);

    const handleEdit = async (id: number) => {
        const requestId = ++editRequestRef.current;

        const cached = persons.find((p) => p.personId === id);
        if (cached) setSelectedPerson(cached);

        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) return;

            const data = normalizePersonFromApi(await res.json());

            if (editRequestRef.current === requestId) {
                setSelectedPerson(data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseEdit = () => {
        editRequestRef.current++;
        setSelectedPerson(null);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Er du sikker på, at du vil slette denne person?")) return;

        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                alert("Fejl ved sletning af personen");
                return;
            }

            const params = new URLSearchParams();
            params.set("page", String(page));
            params.set("pageSize", String(pageSize));

            if (filters.search.trim()) params.set("search", filters.search.trim());
            if (filters.eventId) {
                params.set("eventId", String(filters.eventId));
            }
            if (filters.functionId) {
                params.set("functionId", String(filters.functionId));
            }
            if (filters.country) params.set("country", filters.country);
            if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
            if (filters.dateTo) params.set("dateTo", filters.dateTo);

            if (filters.profileFilter === "with") params.set("onlyWithProfile", "true");
            if (filters.profileFilter === "without") params.set("onlyWithoutProfile", "true");
            if (filters.printFilter === "printed") params.set("onlyPrinted", "true");
            if (filters.printFilter === "notPrinted") params.set("onlyNotPrinted", "true");

            const selectedEventForPrinted = events.find(
                (e) => e.eventID === filters.eventId
            );
            if (
                (filters.printFilter === "printed" || filters.printFilter === "notPrinted") &&
                selectedEventForPrinted
            ) {
                params.set("printedEventId", String(selectedEventForPrinted.eventID));
            }

            const reloadRes = await fetch(`${apiUrl}/api/persons?${params.toString()}`, {
                headers: getAuthHeaders(),
            });

            if (reloadRes.ok) {
                const data: PagedResponse<any> = await reloadRes.json();
                setPersons((data.items ?? []).map(normalizePersonFromApi));
                setTotalCount(data.totalCount ?? 0);
                setTotalPages(data.totalPages ?? 1);
            }
        } catch (err) {
            console.error(err);
            alert("Fejl ved sletning af personen");
        }
    };

    const handleUpdate = async (updatedPerson: PersonFormData) => {
        const dto = {
            ...updatedPerson,
            events: updatedPerson.events ?? [],
            functions: updatedPerson.functions ?? [],
            uploadImageBase64: updatedPerson.uploadImageBase64 ?? "",
            personImagePath: updatedPerson.personImagePath ?? "",
        };

        try {
            const res = await fetch(`${apiUrl}/api/persons/${updatedPerson.personId}`, {
                method: "PUT",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                console.error(await res.text());
                alert("Fejl ved opdatering af personen");
                return;
            }

            const updatedRaw = await res.json();
            const updated = normalizePersonFromApi({
                ...updatedRaw,
                hasImage: updatedRaw.hasImage ?? !!updatedRaw.personImagePath,
            });

            setPersons((prev) => prev.map((p) => (p.personId === updated.personId ? updated : p)));
            setSelectedPerson(null);
        } catch (err) {
            console.error(err);
            alert("Kunne ikke opdatere personen. Tjek API'en.");
        }
    };

    const handleCreatePerson = async (newPerson: PersonFormData) => {
        try {
            const res = await fetch(`${apiUrl}/api/persons/find-duplicates`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(newPerson),
            });

            if (!res.ok) {
                alert("Kunne ikke tjekke for dubletter");
                return;
            }

            const duplicates: Person[] = await res.json();

            console.log("DUPLICATES:", duplicates);

            if (duplicates.length > 0) {
                setPendingPerson(newPerson);
                setDuplicateCandidates(duplicates);
                setAddingPerson(false);        // ✅ close add modal
                setShowDuplicateModal(true);   // open duplicate flow
                return;
            }

            await submitNewPerson(newPerson);

        } catch (err) {
            console.error(err);
            alert("Fejl ved dublet-check");
        }
    };

    const handleUseExisting = async (person: Person) => {
        setShowDuplicateModal(false);
        setDuplicateCandidates(undefined);
        setPendingPerson(null);

        try {
            const res = await fetch(`${apiUrl}/api/persons/${person.personId}`, {
                headers: getAuthHeaders(),
            });

            if (!res.ok) throw new Error("Failed to load person");

            const fullPerson = normalizePersonFromApi(await res.json());

            setAddingPerson(false);   // 🔥 IMPORTANT
            setSelectedPerson(fullPerson);

        } catch (err) {
            console.error(err);
            alert("Kunne ikke hente personen");
        }
    };

    const handleForceCreate = async () => {
        if (!pendingPerson) return;

        await submitNewPerson(pendingPerson);

        setShowDuplicateModal(false);
        setDuplicateCandidates(undefined);
        setPendingPerson(null);
    };

    const submitNewPerson = async (newPerson: PersonFormData) => {
        const dto = {
            ...newPerson,
            userId: newPerson.userId ?? currentUserId,
            events: newPerson.events ?? [],
            functions: newPerson.functions ?? [],
            uploadImageBase64: newPerson.uploadImageBase64 ?? "",
            personImagePath: newPerson.personImagePath ?? "",
        };

        try {
            const res = await fetch(`${apiUrl}/api/persons`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                console.error(await res.text());
                alert("Fejl ved oprettelse af personen");
                return;
            }

            setAddingPerson(false);
            setPage(1);
            setRefreshKey(prev => prev + 1);

        } catch (err) {
            console.error(err);
            alert("Kunne ikke oprette personen. Tjek API'en.");
        }
    };

    const functionsForTargetEvent = useMemo(
        () => getFunctionsForEvent(targetEventId),
        [targetEventId, functionsWithZones]
    );

    const selectedEvent = filters.eventId
        ? events.find(ev => ev.eventID === filters.eventId) ?? null
        : null;

    const personsForPrint = useMemo<PrintPerson[]>(() => {
        if (!selectedEvent) return [];

        return selectedPersonsForIDCard.map((p) => {
            const personFunctionIds = (p.functions ?? []).map(f => f.functionId);

            const functionsInEvent = functionsWithZones.filter(
                (fz) =>
                    fz.eventID === selectedEvent.eventID &&
                    personFunctionIds.includes(fz.functionId)
            );

            const zonesInEvent: Zone[] = [];

            functionsInEvent.forEach((fz) => {
                if (fz.assignedZones) zonesInEvent.push(...fz.assignedZones);
            });

            return {
                ...p,
                functions: functionsInEvent.map((fz) => ({
                    functionId: fz.functionId,
                    functionName: fz.functionName,
                    useDesign2: fz.useDesign2 ?? false,
                })),
                zones: zonesInEvent,
            };
        });
    }, [selectedEvent, selectedPersonsForIDCard, functionsWithZones]);

    const handlePrinted = async (printedPersons: PrintPerson[]) => {
        if (!selectedEvent) return;

        try {
            const payload = printedPersons.map((p) => ({
                personId: p.personId,
                eventId: selectedEvent.eventID,
            }));

            const res = await fetch(`${apiUrl}/api/persons/print-card`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Kunne ikke logge print");

            setPrintedIds((prev = []) =>
                Array.from(new Set([...prev, ...printedPersons.map((p) => p.personId)]))
            );
        } catch (err) {
            console.error(err);
            alert("Fejl ved logging af print");
        }
    };

    const filteredFunctions = useMemo(
        () => getFunctionsForEvent(filters.eventId),
        [filters.eventId, functionsWithZones]
    );

    const visiblePersons = useMemo(() => {
        return persons.filter((p) => {
            // No event selected → show all persons
            if (!filters.eventId) return true;

            // Person has selected event
            const hasEvent = p.events?.some(
                (e) => Number(e.eventID) === Number(filters.eventId)
            );

            // Person has function related to selected event
            const hasFunction = p.functions?.some(
                (f) => Number(f.eventId) === Number(filters.eventId)
            );

            return hasEvent || hasFunction;
        });
    }, [persons, filters.eventId]);

    const handleMoveToEvent = async () => {
        if (!targetEventId) return;

        try {
            const res = await fetch(`${apiUrl}/api/persons/move-to-event`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    personIds: selectedPersonsForIDCard.map(p => p.personId),
                    newEventId: targetEventId,
                    functionIds: targetFunctionIds
                }),
            });

            if (!res.ok) {
                alert("Fejl ved flytning af personer");
                return;
            }

            setPage(1);
            setSelectedPersonsForIDCard([]);
            setTargetEventId(null);
            setTargetFunctionIds([]);
            setRefreshKey(prev => prev + 1); // 🔥 forces reload
        } catch (err) {
            console.error(err);
            alert("Noget gik galt");
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Persons"
                titleDa="Personer"
                descriptionEn="Manage all registered persons"
                descriptionDa="Her kan du se, redigere og slette personer"
                imageSrc={Billede1}
            />

            {errorMsg && <div style={{ padding: 20, color: "crimson" }}>{errorMsg}</div>}

            {!selectedPerson && (
                <div className="personsFiltersWrap">
                    <div className="personsFiltersCard">
                        <div className="personsFiltersGrid3">
                            <select
                                className="personsControl"
                                value={filters.eventId ?? ""}
                                onChange={(e) =>
                                    setFilters((f) => ({
                                        ...f,
                                        eventId: e.target.value ? Number(e.target.value) : null,
                                        functionName: "",
                                    }))
                                }
                            >
                                {!isClub && <option value="">Vælg event</option>}

                                {events.map((ev) => (
                                    <option key={ev.eventID} value={ev.eventID}>
                                        {ev.eventName}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="personsControl"
                                value={filters.functionId ?? ""}
                                disabled={!filters.eventId}
                                onChange={(e) =>
                                    setFilters((f) => ({
                                        ...f,
                                        functionId: e.target.value ? Number(e.target.value) : null,
                                    }))
                                }
                            >
                                <option value="">
                                    {!filters.eventId ? "Vælg event først" : "Vælg funktion"}
                                </option>

                                {filteredFunctions.map((f) => (
                                    <option key={f.functionId} value={f.functionId}>
                                        {f.functionName}
                                    </option>
                                ))}
                            </select>

                            <input
                                className="personsControl personsSearchSpan2"
                                type="text"
                                placeholder="Søg efter navn..."
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((f) => ({ ...f, search: e.target.value }))
                                }
                            />
                        </div>

                        <details className="personsAdvanced">
                            <summary>Udvidede filtre</summary>

                            <div className="personsAdvancedGrid">
                                <div className="personsField">
                                    <label className="personsLabel">Land</label>
                                    <select
                                        className="personsControl"
                                        value={filters.country}
                                        onChange={(e) =>
                                            setFilters((f) => ({ ...f, country: e.target.value }))
                                        }
                                    >
                                        <option value="">Alle lande</option>
                                        {countries.map((c) => (
                                            <option key={String(c)} value={String(c)}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Profilbillede</label>
                                    <select
                                        className="personsControl"
                                        value={filters.profileFilter}
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                profileFilter: e.target.value as Filters["profileFilter"],
                                            }))
                                        }
                                    >
                                        <option value="all">Alle</option>
                                        <option value="with">Kun med profilbillede</option>
                                        <option value="without">Kun uden profilbillede</option>
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Printstatus</label>
                                    <select
                                        className="personsControl"
                                        value={filters.printFilter}
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                printFilter: e.target.value as Filters["printFilter"],
                                            }))
                                        }
                                    >
                                        <option value="all">Alle</option>
                                        <option value="printed">Kun printede</option>
                                        <option value="notPrinted">Kun ikke-printede</option>
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Oprettet fra</label>
                                    <input
                                        className="personsControl"
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) =>
                                            setFilters((f) => ({ ...f, dateFrom: e.target.value }))
                                        }
                                    />
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Oprettet til</label>
                                    <input
                                        className="personsControl"
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) =>
                                            setFilters((f) => ({ ...f, dateTo: e.target.value }))
                                        }
                                    />
                                </div>
                            </div>
                        </details>

                        <div className="personsActionsRow">
                            <button className="btn btn--new" onClick={() => setAddingPerson(true)}>
                                Tilføj Person
                            </button>

                            <button
                                type="button"
                                className="btn btn--default"
                                onClick={() => setFilters(initialFilters)}
                            >
                                Clear Filters
                            </button>

                            {selectedPersonsForIDCard.length > 0 && (
                                <div className="personsActionsRight">

                                    {/* Event selector */}
                                    <select
                                        className="personsControl personsTemplateSelect"
                                        value={targetEventId ?? ""}
                                        onChange={(e) => {
                                            const val = e.target.value ? Number(e.target.value) : null;
                                            setTargetEventId(val);
                                            setTargetFunctionIds([]);
                                        }}
                                    >
                                        <option value="">Vælg destination event</option>
                                        {events.map((ev) => (
                                            <option key={ev.eventID} value={ev.eventID}>
                                                {ev.eventName}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Function selection (much more usable than multi-select) */}
                                    {targetEventId && (
                                        <div className="functionBox">
                                            <div className="functionBoxHeader">
                                                <strong>Vælg funktioner</strong>

                                                <button
                                                    type="button"
                                                    className="btn btn--small"
                                                    onClick={() => {
                                                        if (targetFunctionIds.length === functionsForTargetEvent.length) {
                                                            setTargetFunctionIds([]);
                                                        } else {
                                                            setTargetFunctionIds(functionsForTargetEvent.map(f => f.functionId));
                                                        }
                                                    }}
                                                >
                                                    {targetFunctionIds.length === functionsForTargetEvent.length
                                                        ? "Fjern alle"
                                                        : "Vælg alle"}
                                                </button>
                                            </div>

                                            <div className="functionCheckboxList">
                                                {functionsForTargetEvent.map((f) => (
                                                    <label key={f.functionId} className="functionCheckboxItem">
                                                        <input
                                                            type="checkbox"
                                                            checked={targetFunctionIds.includes(f.functionId)}
                                                            onChange={() => {
                                                                setTargetFunctionIds(prev =>
                                                                    prev.includes(f.functionId)
                                                                        ? prev.filter(id => id !== f.functionId)
                                                                        : [...prev, f.functionId]
                                                                );
                                                            }}
                                                        />
                                                        {f.functionName}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action button */}
                                    <button
                                        className="btn btn--default"
                                        disabled={
                                            !targetEventId ||
                                            selectedPersonsForIDCard.length === 0 ||
                                            targetFunctionIds.length === 0
                                        }
                                        onClick={handleMoveToEvent}
                                    >
                                        Flyt til event
                                    </button>

                                    <select
                                        className="personsControl personsTemplateSelect"
                                        value={selectedTemplateId ?? ""}
                                        onChange={(e) =>
                                            setSelectedTemplateId(
                                                e.target.value ? Number(e.target.value) : null
                                            )
                                        }
                                    >
                                        <option value="">Vælg skabelon</option>

                                        {templates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        className="btn btn-secondary"
                                        disabled={!selectedTemplateId || !selectedEvent}
                                        onClick={() => setShowIDCardPreview(true)}
                                    >
                                        Vis ID Kort
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {addingPerson && !showDuplicateModal && (
                <PersonModalForm
                    initialPerson={null}
                    onSave={handleCreatePerson}
                    onCancel={() => setAddingPerson(false)}
                    allEvents={events}
                    functions={allFunctions}
                    currentUserId={currentUserId}
                />
            )}

            {showDuplicateModal && pendingPerson && (
                <PersonModalForm
                    initialPerson={null}
                    onSave={handleCreatePerson}
                    onCancel={() => {
                        setShowDuplicateModal(false);
                        setDuplicateCandidates(undefined);
                        setPendingPerson(null);
                    }}
                    allEvents={events}
                    functions={allFunctions}
                    currentUserId={currentUserId}
                    duplicateCandidates={duplicateCandidates}
                    onUseExisting={handleUseExisting}
                    onForceCreate={handleForceCreate}
                />
            )}

            {selectedPerson && (
                <PersonModalForm
                    initialPerson={selectedPerson}
                    onSave={handleUpdate}
                    onCancel={handleCloseEdit}
                    allEvents={events}
                    functions={allFunctions}
                    currentUserId={currentUserId}
                />
            )}

            {showIDCardPreview && selectedTemplateId && selectedEvent && (
                <IDCardPrint
                    persons={personsForPrint}
                    template={templates.find((t) => t.id === selectedTemplateId)!}
                    selectedEvent={selectedEvent}
                    functionsWithZones={functionsWithZones}
                    onClose={() => setShowIDCardPreview(false)}
                    onPrinted={handlePrinted}
                />
            )}

            {!selectedPerson && !addingPerson && printedIds && (
                <>
                    <PersonsList
                        persons={visiblePersons}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        tableId="persons-table"
                        onSelectPersons={setSelectedPersonsForIDCard}
                        printedIds={printedIds}
                        selectedEventId={filters.eventId}
                        allFunctions={functionsWithZones}
                    />

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            {loadingPersons
                                ? "Indlæser personer..."
                                : `Viser side ${page} af ${totalPages} · ${totalCount} personer`}
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                className="btn btn--default"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Forrige
                            </button>

                            <button
                                className="btn btn--default"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Næste
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Persons;